import cx from 'classnames'
import { type Variants, motion, AnimatePresence } from 'framer-motion'
import { useKeenSlider } from 'keen-slider/react'
import type { KeenSliderPlugin } from 'keen-slider'
import { useContext, useState } from 'react'
import { type SanityImageFragment } from '@data/sanity/queries/types/image'
import { StringsContext } from '@lib/strings-context'
import { screens } from '@lib/theme'
import Photo from './photo'
import Icon from '@components/icon'

export interface PhotoCarouselProps {
  id?: string
  photos?: SanityImageFragment[]
  hasArrows?: boolean
  hasDots?: boolean
  hasCounter?: boolean
  hasDrag?: boolean
  hasThumbnails?: boolean
  cornerControls?: boolean
  preloadFirstPhoto?: boolean
  className?: string
}

const flipAnimation: Variants = {
  show: {
    y: ['100%', '0%'],
    transition: {
      duration: 1,
      ease: [0.16, 1, 0.3, 1],
      when: 'beforeChildren',
    },
  },
  hide: {
    y: ['0%', '-100%'],
    transition: {
      duration: 1,
      ease: [0.16, 1, 0.3, 1],
      when: 'afterChildren',
    },
  },
}

const WheelControls: KeenSliderPlugin = (slider) => {
  let touchTimeout: ReturnType<typeof setTimeout>
  let position: {
    x: number
    y: number
  }
  let wheelActive: boolean

  function dispatch(e: WheelEvent, name: string) {
    position.x -= e.deltaX
    position.y -= e.deltaY
    slider.container.dispatchEvent(
      new CustomEvent(name, {
        detail: {
          x: position.x,
          y: position.y,
        },
      })
    )
  }

  function wheelStart(e: WheelEvent) {
    position = {
      x: e.pageX,
      y: e.pageY,
    }
    dispatch(e, 'ksDragStart')
  }

  function wheel(e: WheelEvent) {
    dispatch(e, 'ksDrag')
  }

  function wheelEnd(e: WheelEvent) {
    dispatch(e, 'ksDragEnd')
  }

  function eventWheel(e: WheelEvent) {
    e.preventDefault()
    if (!wheelActive) {
      wheelStart(e)
      wheelActive = true
    }
    wheel(e)
    clearTimeout(touchTimeout)
    touchTimeout = setTimeout(() => {
      wheelActive = false
      wheelEnd(e)
    }, 50)
  }

  slider.on('created', () => {
    slider.container.addEventListener('wheel', eventWheel, {
      passive: false,
    })
  })
}

const PhotoCarousel = ({
  id,
  photos,
  hasArrows,
  hasDots,
  hasCounter,
  hasDrag = true,
  hasThumbnails,
  cornerControls,
  preloadFirstPhoto,
  className,
}: PhotoCarouselProps) => {
  const strings = useContext(StringsContext)

  const [currentSlide, setCurrentSlide] = useState(0)
  const [sliderRef, slider] = useKeenSlider<HTMLDivElement>({
    initial: 0,
    selector: '.carousel-slide',
    defaultAnimation: { duration: 800 },
    dragSpeed: 0.8,
    drag: hasDrag,
    slideChanged(slider) {
      const index = slider.track.details.rel
      setCurrentSlide(index)
      thumbnailSlider.current?.moveToIdx(index)
    },
  })

  const [thumbnailRef, thumbnailSlider] = useKeenSlider<HTMLDivElement>(
    {
      initial: 0,
      selector: '.carousel-thumbnail',
      mode: 'free',
      defaultAnimation: { duration: 800 },
      drag: true,
      slides: {
        spacing: 8,
        perView: 'auto',
      },
      breakpoints: {
        [`(min-width: ${screens.md})`]: {
          vertical: true,
          rubberband: false,
        },
      },
    },
    [WheelControls]
  )

  const slideCount = photos?.length ?? 0
  const isFirstSlide = currentSlide === 0
  const isLastSlide = currentSlide === slideCount - 1

  return (
    <div
      id={id}
      className={cx(
        {
          'flex flex-col gap-2 relative md:pr-[calc(6rem+8px)] overflow-hidden':
            hasThumbnails,
        },
        className
      )}
    >
      <div className="relative overflow-hidden grow-0">
        <div
          ref={sliderRef}
          className={cx(
            'flex relative overflow-hidden will-change-transform touch-action touch-action-pan-y',
            {
              'cursor-grab active:cursor-grabbing': hasDrag,
            }
          )}
        >
          {photos?.map((photo, index) => (
            <div
              className="carousel-slide relative flex flex-grow-0 flex-shrink-0 w-full min-h-full overflow-hidden justify-center"
              key={`photo-${photo.asset?.assetId}-${index}`}
            >
              <Photo
                image={photo}
                priority={preloadFirstPhoto && index === 0}
              />
            </div>
          ))}
        </div>

        {hasArrows && slideCount > 1 && (
          <>
            <button
              onClick={() => slider.current?.prev()}
              aria-label={strings.carouselLeftArrowLabel}
              disabled={isFirstSlide}
              className={cx(
                'absolute left-3 top-1/2 transform -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full border border-gray-200 bg-transparent overflow-hidden transition-colors duration-300',
                {
                  'opacity-40': isFirstSlide,
                  'absolute bottom-3 right-11': cornerControls,
                }
              )}
            >
              <Icon id={`prev-${id}`} name="LeftArrow" />
            </button>

            <button
              onClick={() => slider.current?.next()}
              aria-label={strings.carouselRightArrowLabel}
              disabled={isLastSlide}
              className={cx(
                'absolute right-3 top-1/2 transform -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full border border-gray-200 bg-transparent overflow-hidden transition-colors duration-300 text-gray-700 hover:text-black',
                {
                  'opacity-40': isLastSlide,
                  'absolute bottom-3 right-3': cornerControls
                }
              )}
            >
              <Icon id={`next-${id}`} name="RightArrow" />
            </button>
          </>
        )}

        {(hasArrows || hasDots) && slideCount > 1 && (
          <div
            className={cx(
              'flex absolute bottom-0 inset-x-0 transform pointer-events-none',
              {
                'justify-center translate-y-1/2': !cornerControls,
                'justify-start translate-y-0': cornerControls,
              }
            )}
          >
            <div
              className={cx(
                'flex flex-row items-center rounded-full p-1 pointer-events-auto',
                {
                  'relative bg-pageBG border': !cornerControls,
                  'static bg-transparent border-none': cornerControls,
                }
              )}
            >

              <div
                className={cx(
                  'flex items-center justify-center relative h-8 mx-2',
                  { 'mx-0 pl-1 pb-3': cornerControls }
                )}
              >
                {hasDots && (
                  <div
                    className={cx('relative flex items-center h-8', {
                      'ml-3': cornerControls,
                    })}
                  >
                    {[...Array(slideCount).keys()].map((index) => (
                      <button
                        key={index}
                        onClick={() => slider.current?.moveToIdx(index)}
                        aria-label={strings.carouselDotLabel.replace(
                          /{index}/gi,
                          (index + 1).toString()
                        )}
                        className={cx(
                          'p-1 bg-transparent',
                          'before:block before:relative before:w-2 before:h-2 before:bg-pageBG before:rounded-full before:opacity-30',
                          'before:transition-carousel-dots',
                          'hover:before:opacity-100',
                          {
                            'before:opacity-100 transform scale-150':
                              currentSlide === index,
                          }
                        )}
                      />
                    ))}
                  </div>
                )}

                {hasCounter && (
                  <div
                    className={cx('relative grid gap-px rounded-full', {
                      'grid-cols-2 h-8 bg-pageBG text-pageText':
                        !cornerControls,
                      'grid-cols-3 h-auto bg-transparent text-pageText select-none':
                        cornerControls,
                    })}
                  >
                    {!cornerControls && (
                      <div className="absolute left-1/2 transform -translate-x-1/2 inset-y-3 w-px bg-pageText opacity-30" />
                    )}
                    <div
                      className={cx('relative flex flex-col p-2 pl-3', {
                        'text-xs font-semibold overflow-hidden':
                          !cornerControls,
                        'px-0 text-base font-normal text-center overflow-visible':
                          cornerControls,
                      })}
                    >
                      <div className="relative overflow-hidden">
                        <AnimatePresence initial={false}>
                          <motion.span
                            key={currentSlide + 1}
                            initial="hide"
                            animate="show"
                            exit="hide"
                            variants={flipAnimation}
                            className="block will-change-transform absolute inset-x-0 top-0 last:static last:inset-x-auto last:top-auto"
                          >
                            {currentSlide + 1}
                          </motion.span>
                        </AnimatePresence>
                      </div>
                    </div>

                    {cornerControls && (
                      <div className="relative flex flex-col p-2 px-0 text-base font-normal text-center overflow-visible">
                        <span>{strings.carouselCounterText}</span>
                      </div>
                    )}

                    <div
                      className={cx('relative flex flex-col p-2 pr-3', {
                        'text-xs font-semibold overflow-hidden':
                          !cornerControls,
                        'px-0 text-base font-normal text-center overflow-visible':
                          cornerControls,
                      })}
                    >
                      <span>{slideCount}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {hasThumbnails && (
        <div
          ref={thumbnailRef}
          className={cx('md:absolute md:right-0 md:inset-y-0 flex md:flex-col')}
        >
          {photos?.map((photo, index) => (
            <button
              className={cx(
                'carousel-thumbnail hover:opacity-60 duration-300 transition-opacity',
                { 'opacity-60': index === currentSlide }
              )}
              onClick={() => {
                setCurrentSlide(index)
                slider.current?.moveToIdx(index)
              }}
              key={`thumbnail-${photo.asset?.assetId}`}
            >
              <Photo
                image={photo}
                fill
                sizes="96px"
                className="relative w-14 h-14 xs:h-20 xs:w-20 md:w-24 md:h-24"
                imageClassName="object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default PhotoCarousel
